import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useMemo, useState} from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {TimingEstimateResult} from 'studies/progress/useTimingEstimate';
import {prepareInitialExperimentalInputs} from 'studies/typed/process-development/ExperimentalInputs';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {StudyInaccessibleContentBanner, StudyMissingContentBanner} from '../utils/ui/StudyQueryBanners';
import {
  ProcessDevelopmentStudyDesignTabContentsQuery_study_StudyNode_procedureRun_ProcedureRunNode_templateProcedureFlowchartVersion_ProcedureFlowchartVersionNode as AccessibleTemplateProcedureFlowchartVersionNode,
  ProcessDevelopmentStudyDesignTabContentsQuery_study_StudyNode_procedureRun_ProcedureRunNode as ProcedureRunNode,
  ProcessDevelopmentStudyDesignTabContentsQuery,
  ProcessDevelopmentStudyDesignTabContentsQueryVariables,
} from './__generated/ProcessDevelopmentStudyDesignTabContentsQuery';
import getStudyDesignFromProcedureRun, {
  STUDY_DESIGN_FROM_PROCEDURE_RUN_FRAGMENT,
} from './getStudyDesignFromProcedureRun';
import {ProcessDevelopmentStudyPlanEditor} from './ProcessDevelopmentStudyPlanEditor';
import {useExperimentalInputsContextProperties} from './useProcessDevelopmentStudyState';

const NO_ESTIMATE: TimingEstimateResult = {stale: false, executionPlan: null};

export const PROCESS_DEVELOPMENT_STUDY_DESIGN_TAB_CONTENTS_QUERY: DocumentNode = gql`
  query ProcessDevelopmentStudyDesignTabContentsQuery($studyId: StrictString!)
  @skipAudit
  @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        studySchema {
          id
          studyType
        }
        procedureRun {
          ...GetStudyDesignFromProcedureRunGraphQL
          ... on ProcedureRunNode {
            id
            procedureType
            conditions {
              id
              name
              description
              procedureRunConditionReplicates {
                id
              }
            }
            templateProcedureFlowchartVersion {
              ...ExperimentalInputsFlowchartGraphQL
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ProcedureFlowchartVersion').fragment}
  ${STUDY_DESIGN_FROM_PROCEDURE_RUN_FRAGMENT}
`;

function ProcessDevelopmentStudyDesignTabContents(props: {
  procedureRun: ProcedureRunNode & {
    templateProcedureFlowchartVersion: AccessibleTemplateProcedureFlowchartVersionNode;
  };
}): JSX.Element {
  const {procedureRun} = props;
  const {experimentalInputs: initialExperimentalInputs, templateFlowchart} = useMemo(
    () => getStudyDesignFromProcedureRun(procedureRun),
    [procedureRun]
  );
  const [selectedMethodId, setSelectedMethodId] = useState(templateFlowchart.nodes[0]?.id ?? null);
  // While read-only, SimpleTableEditable will do a no-op update the inputs after the initial evaluation.
  const [experimentalInputs, _setExperimentalInputs] = useState<ExperimentalInputs>(
    prepareInitialExperimentalInputs(procedureRun.conditions.length, templateFlowchart.nodes, {
      prevExperimentalInputValues: initialExperimentalInputs,
      prevConfirmationMethods: initialExperimentalInputs,
    })
  );
  const conditions = useMemo(
    () =>
      procedureRun.conditions.map((condition) => ({
        numReplicates: condition.procedureRunConditionReplicates.length,
        name: condition.name,
        description: condition.description ?? undefined,
      })),
    [procedureRun]
  );

  const context = useExperimentalInputsContextProperties(
    conditions,
    experimentalInputs,
    null,
    procedureRun.templateProcedureFlowchartVersion,
    procedureRun.procedureType,
    true
  );
  // Ideally `context` would have a conditional return type based on experimentalInputs null-ness.
  assert(context != null, 'Expected context to be non-null');

  return selectedMethodId == null ? (
    // Unexpected since we should prevent creating a study if there are no methods, but handle it gracefully.
    <div className={css(styles.bufferTabWrapper)}>
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text={`No ${getProcedureUIString(ProceduresStringKeys.METHOD, procedureRun.procedureType, {
          stringCase: StringCase.LOWER,
          plural: true,
        })} have been configured for this ${getProcedureUIString(
          ProceduresStringKeys.PROCEDURE,
          procedureRun.procedureType,
          {
            stringCase: StringCase.LOWER,
          }
        )}.`}
      />
    </div>
  ) : (
    <>
      <ProcessDevelopmentStudyPlanEditor
        conditions={procedureRun.conditions.map((condition) => ({
          numReplicates: condition.procedureRunConditionReplicates.length,
          name: condition.name,
          description: condition.description ?? undefined,
        }))}
        setConditions={noop}
        experimentalInputs={experimentalInputs}
        setExperimentalInputs={noop}
        context={context}
        procedureFlowchart={templateFlowchart}
        procedureMethods={templateFlowchart.nodes}
        selectedMethodId={selectedMethodId}
        setSelectedMethodId={setSelectedMethodId}
        methodExecutionLimitState={{
          isOverLimit: false,
          remainingReplicates: 0,
          replicateLimit: 0,
          replicateCount: 0,
        }}
        estimate={NO_ESTIMATE}
        procedureType={procedureRun.procedureType}
        showExperimentName={false}
      />
    </>
  );
}

export default function QueryingProcessDevelopmentStudyDesignTabContents({
  studyId,
}: {
  studyId: string;
}): JSX.Element {
  const result = useBenchlingQuery<
    ProcessDevelopmentStudyDesignTabContentsQuery,
    ProcessDevelopmentStudyDesignTabContentsQueryVariables
  >(PROCESS_DEVELOPMENT_STUDY_DESIGN_TAB_CONTENTS_QUERY, {variables: {studyId}});
  if (result.type !== QueryResultType.SUCCESS) {
    return <CenteredHorizontalThrobber />;
  } else {
    const {data} = result;
    if (isInaccessible(data.study)) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.study}
          modelType={ModelTypeConstants.STUDY}
          displayErrorText="This study is no longer accessible. Try refreshing the page if this is unexpected."
        />
      );
    } else if (data.study.procedureRun == null) {
      return (
        <StudyMissingContentBanner
          parentRenderFunctionName={ProcessDevelopmentStudyDesignTabContents.name}
          modelType={ModelTypeConstants.PROCEDURE_RUN}
          parentId={studyId}
          displayErrorText="The experiment design associated with this study could not be found."
        />
      );
    } else if (isInaccessible(data.study.procedureRun)) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.study.procedureRun}
          modelType={ModelTypeConstants.PROCEDURE_RUN}
          displayErrorText="The experiment design of this study is not accessible. The experiment run associated with this study may have been moved into a project that you do not have access to."
        />
      );
    } else if (isInaccessible(data.study.procedureRun.templateProcedureFlowchartVersion)) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.study.procedureRun.templateProcedureFlowchartVersion}
          modelType={ModelTypeConstants.PROCEDURE}
          displayErrorText="The experiment design of this study is not accessible. The recipe associated with this study may have been moved into a template collection that you do not have access to."
        />
      );
    } else {
      return (
        <ProcessDevelopmentStudyDesignTabContents
          procedureRun={{
            ...data.study.procedureRun,
            templateProcedureFlowchartVersion: data.study.procedureRun.templateProcedureFlowchartVersion,
          }}
        />
      );
    }
  }
}

const styles = StyleSheet.create({
  bufferTabWrapper: {
    paddingTop: 10,
    paddingBottom: 10,
  },
});
